import React, { useState, useEffect, useCallback, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Collapse,
  Typography,
} from "@material-ui/core";
import {
  FilterList as FilterIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
} from "@material-ui/icons";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(0.5, 1),
    borderBottom:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,0.06)"
        : "1px solid rgba(0,0,0,0.06)",
  },
  toggleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  filtersRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    alignItems: "center",
  },
  filterControl: {
    minWidth: 140,
    maxWidth: 180,
  },
  activeFilters: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    marginLeft: theme.spacing(1),
  },
  chip: {
    height: 22,
    fontSize: "0.7rem",
  },
  label: {
    fontSize: "0.78rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
}));

const PRIORITY_OPTIONS = [
  { value: "urgent", label: "Urgente" },
  { value: "high", label: "Alta" },
  { value: "normal", label: "Normal" },
  { value: "low", label: "Baixa" },
];

/**
 * BoardFilters — barra de filtros avançados no topo do board.
 *
 * Props:
 *  - filters: { userIds, queueIds, whatsappIds, priority, tagIds }
 *  - onFilterChange: (newFilters) => void
 *  - companyId: number
 *  - users: array — cached user list
 *  - queues: array — cached queue list
 */
const BoardFilters = ({ filters = {}, onFilterChange, companyId }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [users, setUsers] = useState([]);
  const [queues, setQueues] = useState([]);

  // Load users + queues on first expand
  useEffect(() => {
    if (!expanded || !companyId) return;
    let cancelled = false;

    const fetchData = async () => {
      try {
        const [usersRes, queuesRes] = await Promise.all([
          api.get("/users/list", { params: { companyId } }),
          api.get("/queue", { params: { companyId } }),
        ]);
        if (!cancelled) {
          setUsers(usersRes.data || []);
          setQueues(queuesRes.data || []);
        }
      } catch {
        // silencioso
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [expanded, companyId]);

  const activeCount = useMemo(() => {
    let count = 0;
    if (filters.userIds?.length) count++;
    if (filters.queueIds?.length) count++;
    if (filters.priority?.length) count++;
    if (filters.walletUserIds?.length) count++;
    if (filters.searchText?.trim()) count++;
    return count;
  }, [filters]);

  const handleChange = useCallback(
    (field, value) => {
      onFilterChange({ ...filters, [field]: value });
    },
    [filters, onFilterChange]
  );

  const handleClear = useCallback(() => {
    onFilterChange({});
  }, [onFilterChange]);

  return (
    <Box className={classes.root}>
      <Box className={classes.toggleRow}>
        <Tooltip title={expanded ? "Recolher filtros" : "Expandir filtros"}>
          <IconButton size="small" onClick={() => setExpanded((prev) => !prev)}>
            <FilterIcon fontSize="small" />
            {expanded ? (
              <CollapseIcon fontSize="small" />
            ) : (
              <ExpandIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        <Typography className={classes.label}>
          Filtros {activeCount > 0 ? `(${activeCount})` : ""}
        </Typography>
        {activeCount > 0 && (
          <>
            <Box className={classes.activeFilters}>
              {filters.priority?.map((p) => (
                <Chip key={p} className={classes.chip} label={p} size="small" onDelete={() => {
                  handleChange("priority", filters.priority.filter((x) => x !== p));
                }} />
              ))}
            </Box>
            <Tooltip title="Limpar filtros">
              <IconButton size="small" onClick={handleClear}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      <Collapse in={expanded}>
        <Box className={classes.filtersRow}>
          {/* Vendedor */}
          <FormControl
            variant="outlined"
            size="small"
            className={classes.filterControl}
          >
            <InputLabel>Vendedor</InputLabel>
            <Select
              multiple
              value={filters.userIds || []}
              onChange={(e) => handleChange("userIds", e.target.value)}
              label="Vendedor"
              renderValue={(sel) =>
                sel.length === 0
                  ? "Todos"
                  : `${sel.length} selecionado(s)`
              }
            >
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Fila */}
          <FormControl
            variant="outlined"
            size="small"
            className={classes.filterControl}
          >
            <InputLabel>Fila</InputLabel>
            <Select
              multiple
              value={filters.queueIds || []}
              onChange={(e) => handleChange("queueIds", e.target.value)}
              label="Fila"
              renderValue={(sel) =>
                sel.length === 0
                  ? "Todas"
                  : `${sel.length} selecionada(s)`
              }
            >
              {queues.map((q) => (
                <MenuItem key={q.id} value={q.id}>
                  {q.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Prioridade */}
          <FormControl
            variant="outlined"
            size="small"
            className={classes.filterControl}
          >
            <InputLabel>Prioridade</InputLabel>
            <Select
              multiple
              value={filters.priority || []}
              onChange={(e) => handleChange("priority", e.target.value)}
              label="Prioridade"
              renderValue={(sel) =>
                sel.length === 0
                  ? "Todas"
                  : sel.join(", ")
              }
            >
              {PRIORITY_OPTIONS.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Carteira (wallet view) */}
          <FormControl
            variant="outlined"
            size="small"
            className={classes.filterControl}
          >
            <InputLabel>Carteira</InputLabel>
            <Select
              multiple
              value={filters.walletUserIds || []}
              onChange={(e) => handleChange("walletUserIds", e.target.value)}
              label="Carteira"
              renderValue={(sel) =>
                sel.length === 0
                  ? "Todas"
                  : `${sel.length} selecionada(s)`
              }
            >
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Busca texto */}
          <TextField
            variant="outlined"
            size="small"
            placeholder="Buscar..."
            value={filters.searchText || ""}
            onChange={(e) => handleChange("searchText", e.target.value)}
            style={{ minWidth: 140 }}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

export default React.memo(BoardFilters);
